import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  ArrowBack,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface CoffeeBean {
  id: string;
  coordinate: {
    latitude: number;
    longitude: number;
  };
  type: 'normal' | 'premium';
  points: number;
  spawnedAt: Date;
  expiresAt: Date;
  collectedBy: string[];
}

interface Stats {
  totalBeans: number;
  normalBeans: number;
  premiumBeans: number;
  expiredBeans: number;
  collected: number;
  totalCollections: number;
}

const BeanManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [beans, setBeans] = useState<CoffeeBean[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalBeans: 0,
    normalBeans: 0,
    premiumBeans: 0,
    expiredBeans: 0,
    collected: 0,
    totalCollections: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [openSpawnDialog, setOpenSpawnDialog] = useState(false);

  // Form states
  const [spawnData, setSpawnData] = useState({
    latitude: 41.0082,
    longitude: 28.9784,
    type: 'normal' as 'normal' | 'premium',
    count: 1,
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [beansResponse, statsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/beans`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/beans/stats`, getAuthHeaders()),
      ]);

      if (beansResponse.data.success) {
        setBeans(beansResponse.data.data);
      }
      if (statsResponse.data.success) {
        setStats(statsResponse.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSpawnBean = async () => {
    try {
      setError('');
      const response = await axios.post(
        `${API_BASE_URL}/api/beans/spawn`,
        spawnData,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess(`${spawnData.count} adet bean spawn edildi!`);
        setOpenSpawnDialog(false);
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Spawn sırasında hata oluştu');
    }
  };

  const handleDeleteBean = async (id: string) => {
    if (!window.confirm('Bu bean\'i silmek istediğinizden emin misiniz?')) return;
    try {
      setError('');
      const response = await axios.delete(
        `${API_BASE_URL}/api/beans/${id}`,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Bean silindi!');
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Silme sırasında hata oluştu');
    }
  };

  const handleDeleteExpired = async () => {
    if (!window.confirm('Tüm süresi dolmuş bean\'leri silmek istediğinizden emin misiniz?')) return;
    try {
      setError('');
      const response = await axios.delete(
        `${API_BASE_URL}/api/beans/expired`,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess(`Süresi dolmuş bean'ler silindi!`);
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Silme sırasında hata oluştu');
    }
  };

  const isExpired = (expiresAt: Date): boolean => {
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/admin/dashboard')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            ☕ Coffee Bean Yönetimi
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Toplam Bean
              </Typography>
              <Typography variant="h4">{stats.totalBeans}</Typography>
              <Typography variant="caption" color="primary.main">
                {stats.normalBeans} normal / {stats.premiumBeans} premium
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Toplanan Bean
              </Typography>
              <Typography variant="h4">{stats.collected}</Typography>
              <Typography variant="caption" color="success.main">
                {stats.totalCollections} toplam collection
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Süresi Dolmuş
              </Typography>
              <Typography variant="h4">{stats.expiredBeans}</Typography>
              <Typography variant="caption" color="error.main">
                Temizlenebilir
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Aktif Bean
              </Typography>
              <Typography variant="h4">{stats.totalBeans - stats.expiredBeans}</Typography>
              <Typography variant="caption">Haritada</Typography>
            </CardContent>
          </Card>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenSpawnDialog(true)}
            >
              Bean Spawn Et
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
            >
              Yenile
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteExpired}
              disabled={stats.expiredBeans === 0}
            >
              Süresi Dolmuş Beanları Sil ({stats.expiredBeans})
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tip</TableCell>
                  <TableCell>Konum</TableCell>
                  <TableCell>Puan</TableCell>
                  <TableCell>Spawn Tarihi</TableCell>
                  <TableCell>Son Kullanma</TableCell>
                  <TableCell>Toplayan</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {beans.map((bean) => (
                  <TableRow
                    key={bean.id}
                    sx={{
                      backgroundColor: isExpired(bean.expiresAt) ? 'rgba(244, 67, 54, 0.1)' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={bean.type === 'premium' ? '⭐ Premium' : '☕ Normal'}
                        color={bean.type === 'premium' ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <a
                        href={`https://www.google.com/maps?q=${bean.coordinate.latitude},${bean.coordinate.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationIcon fontSize="small" />
                          {bean.coordinate.latitude.toFixed(4)}, {bean.coordinate.longitude.toFixed(4)}
                        </Box>
                      </a>
                    </TableCell>
                    <TableCell>{bean.points}</TableCell>
                    <TableCell>{new Date(bean.spawnedAt).toLocaleString('tr-TR')}</TableCell>
                    <TableCell>
                      <Box sx={{ color: isExpired(bean.expiresAt) ? 'error.main' : 'inherit' }}>
                        {new Date(bean.expiresAt).toLocaleString('tr-TR')}
                        {isExpired(bean.expiresAt) && ' (Dolmuş)'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${bean.collectedBy.length} kişi`}
                        size="small"
                        color={bean.collectedBy.length > 0 ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => handleDeleteBean(bean.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Spawn Dialog */}
        <Dialog open={openSpawnDialog} onClose={() => setOpenSpawnDialog(false)}>
          <DialogTitle>Bean Spawn Et</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Manuel bean spawn. Otomatik spawn için Cloud Function kullanın.
            </Alert>
            <TextField
              fullWidth
              margin="normal"
              type="number"
              label="Latitude"
              value={spawnData.latitude}
              onChange={(e) => setSpawnData({ ...spawnData, latitude: parseFloat(e.target.value) })}
              inputProps={{ step: 0.000001 }}
            />
            <TextField
              fullWidth
              margin="normal"
              type="number"
              label="Longitude"
              value={spawnData.longitude}
              onChange={(e) => setSpawnData({ ...spawnData, longitude: parseFloat(e.target.value) })}
              inputProps={{ step: 0.000001 }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Tip</InputLabel>
              <Select
                value={spawnData.type}
                label="Tip"
                onChange={(e) => setSpawnData({ ...spawnData, type: e.target.value as 'normal' | 'premium' })}
              >
                <MenuItem value="normal">☕ Normal (1 puan)</MenuItem>
                <MenuItem value="premium">⭐ Premium (2 puan)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              type="number"
              label="Adet"
              value={spawnData.count}
              onChange={(e) => setSpawnData({ ...spawnData, count: parseInt(e.target.value) })}
              helperText="Birden fazla bean spawn eder (küçük offset ile)"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSpawnDialog(false)}>İptal</Button>
            <Button variant="contained" onClick={handleSpawnBean}>
              Spawn Et
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default BeanManagementPage;
